import { aircraftQuery } from "@common/api/aircraft"
import { atlPageGroupPages, atlPagesInfiniteQuery } from "@common/api/atl"
import { ensureListData } from "@common/api/utils"
import { Button } from "@common/components/ui/button"
import { WO_PAGES } from "@common/flags"
import { useMaxWidth } from "@common/hooks/use-mobile"
import { useHasPermission } from "@common/hooks/use-permissions"
import {
  createFileRoute,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router"
import { Suspense } from "react"
import { ActiveFilterBar } from "@/components/layout/active-filter-bar"
import { ColumnChips } from "@/components/layout/column-chips"
import { DETAIL_SHEET_BREAKPOINT } from "@/components/layout/detail-panel"
import {
  type Display,
  DisplayOptions,
} from "@/components/layout/display-options"
import { FilterButton } from "@/components/layout/filter-button"
import { ListPage, type Pane } from "@/components/layout/list-page"
import { ListToolbar } from "@/components/layout/list-toolbar"
import { NameFilter } from "@/components/layout/name-filter"
import { useColumnSizing } from "@/hooks/use-column-layout"
import { useWidth } from "@/hooks/use-width"
import { fitColumns } from "@/lib/column-widths"
import { requireFeatureFlag } from "@/lib/require-feature-flag"
import { requirePermission } from "@/lib/require-permission"
import { FLEET } from "@/routes/_layout/aircraft/-lib/search"
import { columnsParam } from "@/routes/_layout/maintenance-programmes_/$programmeId/-lib/table"
import { TechLogTable } from "./-components/tech-log-table"
import {
  COLUMN_LABELS,
  COLUMN_SIZING,
  COLUMNS,
  type Column,
  GROUPING_LABELS,
  GROUPINGS,
  type Grouping,
} from "./-lib/columns"
import {
  displayOf,
  hasTechLogsFilters,
  NO_TECH_LOGS_FILTERS,
  type TechLogsSearch,
  techLogsFilterConfigs,
  techLogsParams,
  techLogsSearchSchema,
} from "./-lib/search"

const PANES: string[] = ["/_layout/tech-logs/$pageId", "/_layout/tech-logs/new"]

export const Route = createFileRoute("/_layout/tech-logs")({
  staticData: { breadcrumb: "Tech logs" },
  validateSearch: (search) => techLogsSearchSchema.parse(search),
  beforeLoad: async ({ context: { queryClient } }) => {
    await requirePermission(queryClient, "atl_page:read", "/")
    await requireFeatureFlag(queryClient, WO_PAGES)
  },
  loaderDeps: ({ search }) => ({
    pages: techLogsParams(search),
    grouped: Boolean(displayOf(search).group),
  }),
  loader: ({ context: { queryClient }, deps }) => {
    void queryClient.prefetchQuery(aircraftQuery(FLEET))
    return ensureListData({
      grouped: deps.grouped,
      groups: () =>
        queryClient.ensureQueryData(atlPageGroupPages.groups(deps.pages)),
      flat: () =>
        queryClient.ensureInfiniteQueryData(atlPagesInfiniteQuery(deps.pages)),
    })
  },
  component: TechLogsPage,
})

function useUpdateSearch(): (patch: Partial<TechLogsSearch>) => void {
  const navigate = useNavigate()
  return (patch) =>
    navigate({
      to: ".",
      search: (previous) => ({ ...previous, ...patch }),
      replace: true,
    })
}

function usePane(): Pane {
  const leaf = useRouterState({
    select: (state) => state.matches.at(-1)?.routeId,
  })
  const { log_scan, wide } = Route.useSearch()
  const compact = useMaxWidth(DETAIL_SHEET_BREAKPOINT)
  if (leaf === undefined || !PANES.includes(leaf) || compact) return "closed"
  return wide || log_scan !== undefined ? "full" : "split"
}

function groupingSearch({
  group,
  subgroup,
}: Display<Grouping>): Partial<TechLogsSearch> {
  return {
    group: group === "status" ? undefined : (group ?? "none"),
    subgroup,
  }
}

function TechLogsPage(): React.ReactElement {
  const search = Route.useSearch()
  const update = useUpdateSearch()
  const filtered = hasTechLogsFilters(search)
  const display = displayOf(search)
  const [ref, width] = useWidth<HTMLDivElement>()
  const sizing = useColumnSizing(display.columns, COLUMN_SIZING)
  const fit = fitColumns(display.columns, width, sizing)
  const canCreate = useHasPermission("atl_page:create")

  return (
    <ListPage
      toolbar={
        <ListToolbar
          actions={
            <>
              <NameFilter
                search={search.search ?? ""}
                onSearch={(next) => update({ search: next || undefined })}
                placeholder="Search tech logs…"
              />
              <FilterButton
                active={filtered}
                configs={techLogsFilterConfigs}
                onChange={update}
              />
              <DisplayOptions
                groupings={GROUPINGS}
                labels={GROUPING_LABELS}
                group={display.group}
                subgroup={display.subgroup}
                onChange={(next) => update(groupingSearch(next))}
              >
                <ColumnChips<Column>
                  options={COLUMNS}
                  labels={COLUMN_LABELS}
                  fixed={["page"]}
                  columns={display.columns}
                  hidden={fit.hidden}
                  onChange={(next) =>
                    update({ columns: columnsParam(next, COLUMNS) })
                  }
                />
              </DisplayOptions>
              {canCreate && (
                <Button asChild size="sm">
                  <Link to="/tech-logs/new" search={true}>
                    New tech log
                  </Link>
                </Button>
              )}
            </>
          }
        />
      }
      pane={usePane()}
      detail={<Outlet />}
      scrollRef={ref}
    >
      <ActiveFilterBar
        active={filtered}
        configs={techLogsFilterConfigs}
        values={search}
        onChange={update}
        onClear={() => update(NO_TECH_LOGS_FILTERS)}
      />
      <Suspense>
        <TechLogTable
          columns={fit.shown}
          onClear={() => update({ ...NO_TECH_LOGS_FILTERS, search: undefined })}
          onUpdate={update}
        />
      </Suspense>
    </ListPage>
  )
}
